<script>
    import {
        vulnerabilities,
        searchVulnerabilities,
    } from "./vulnerabilities.state.svelte.js";
    import AdvancedSearch from "../../components/powerTable/advanced-search/index.svelte";
    import tagService from "../tag/service.js";
    import BreadCrumNav from "../../components/breadcrum-nav.svelte";
    import SeverityTotals from "./_components/severityTotals/v2-severity-totals.svelte";
    import statisticService from "../dashboard/service.js";
    import List from "./_components/list.svelte";
    import VulnerabilityTitleRow from "../../components/powerTable/vulnerability-title-row.svelte";
    import powerTableService from "../../services/powerTable.js";
    import Spinner from "../../components/spinner.svelte";
    import VulsByKind from "./_components/graphics/vuls-by-kind.svelte";
    import TopFiveVulsByFindings from "./_components/graphics/top-five-vuls-by-findings.svelte";
    import { goto, params } from "@roxi/routify";
    import { format } from "svelte-i18n";
    import { untrack } from "svelte";
    import { token } from "../user/user.state.svelte.js";

    // Safely bind router navigation synchronously at root level during setup
    const redirect = $goto;

    let isInitialLoading = $state(true);
    let tags = $state([]);
    let associations = $state([]);
    let cvssValues = $state([]);
    let epssValues = $state([]);
    let inputs = $state([]);

    // Reactive two-way mapping: Reconstructs valid range strings and strictly whitelists
    // valid input names so AdvancedSearch never queries non-existent DOM nodes.
    let advancedSearchFilter = $derived.by(() => {
        const p = { ...($params || {}) };
        const cleanFilters = {};

        // Dynamically collect all valid input names from our built inputs array
        const validNames = new Set();
        if (Array.isArray(inputs)) {
            for (const group of inputs) {
                if (Array.isArray(group)) {
                    for (const item of group) {
                        if (item && item.name) validNames.add(item.name);
                    }
                }
            }
        }

        // 1. Reconstruct CVSS range string safely with absolute boundary fallbacks (0 to 10)
        if (p.cvss__gte !== undefined || p.cvss__lte !== undefined || p.cvss !== undefined) {
            let min = 0;
            let max = 10;

            if (p.cvss !== undefined && p.cvss !== null && p.cvss !== "") {
                const parts = String(p.cvss).split(";").map((x) => x.trim());
                const minStr = parts[0] || "";
                const maxStr = parts.length >= 2 ? (parts[1] || "") : "";
                if (minStr !== "" && !isNaN(parseFloat(minStr))) min = parseFloat(minStr);
                if (maxStr !== "" && !isNaN(parseFloat(maxStr))) max = parseFloat(maxStr);
                else if (parts.length === 1 && minStr !== "" && !isNaN(parseFloat(minStr))) {
                    min = parseFloat(minStr);
                    max = 10;
                }
            }
            if (p.cvss__gte !== undefined && !isNaN(parseFloat(p.cvss__gte))) {
                min = parseFloat(p.cvss__gte);
            }
            if (p.cvss__lte !== undefined && !isNaN(parseFloat(p.cvss__lte))) {
                max = parseFloat(p.cvss__lte);
            }

            if (min < 0 || isNaN(min)) min = 0;
            if (max > 10 || isNaN(max)) max = 10;

            cleanFilters.cvss = `${min};${max}`;
        }

        // 2. Reconstruct EPSS range string safely with absolute boundary fallbacks (0% to 100%)
        if (p.epss__gte !== undefined || p.epss__lte !== undefined || p.epss !== undefined) {
            let min = 0;
            let max = 100;

            if (p.epss !== undefined && p.epss !== null && p.epss !== "") {
                const parts = String(p.epss).split(";").map((x) => x.trim());
                const minStr = parts[0] ? parts[0].replace("%", "") : "";
                const maxStr = parts.length >= 2 ? (parts[1] ? parts[1].replace("%", "") : "") : "";
                if (minStr !== "" && !isNaN(parseFloat(minStr))) min = Math.round(parseFloat(minStr));
                if (maxStr !== "" && !isNaN(parseFloat(maxStr))) max = Math.round(parseFloat(maxStr));
                else if (parts.length === 1 && minStr !== "" && !isNaN(parseFloat(minStr))) {
                    min = Math.round(parseFloat(minStr));
                    max = 100;
                }
            }
            if (p.epss__gte !== undefined && !isNaN(parseFloat(p.epss__gte))) {
                min = Math.round(parseFloat(p.epss__gte) * 100);
            }
            if (p.epss__lte !== undefined && !isNaN(parseFloat(p.epss__lte))) {
                max = Math.round(parseFloat(p.epss__lte) * 100);
            }

            if (min < 0 || isNaN(min)) min = 0;
            if (max > 100 || isNaN(max)) max = 100;

            cleanFilters.epss = `${min}%;${max}%`;
        }

        // 3. Only copy over whitelisted parameters that correspond to actual physical form inputs
        for (let [key, val] of Object.entries(p)) {
            if (key === "cvss" || key === "epss" || key === "cvss__gte" || key === "cvss__lte" || key === "epss__gte" || key === "epss__lte") continue;
            if (validNames.has(key) && val !== undefined && val !== null && val !== "") {
                cleanFilters[key] = val;
            }
        }

        return cleanFilters;
    });

    const getTags = async (config = { filter: {} }) => {
        tags = await tagService.search("v2/tag/", config);
        return tags;
    };

    const getTagAssociations = async () => {
        associations = await tagService.search("v2/tag/association/", {
            filter: { target_object_type: "Vulnerability" },
        });
        return associations;
    };

    const createCVSSvalues = () => {
        const vals = [];
        for (let i = 0; i <= 100; i++) {
            vals.push(`${i / 10}`);
        }
        return vals;
    };

    const createEPSSvalues = () => {
        const vals = [];
        for (let i = 0; i <= 100; i++) {
            vals.push(i + "%");
        }
        return vals;
    };

    // Safely parses range strings ("5;8") or single-value selections ("5;") into backend boundaries
    const handleFiltersConfig = (rawFilters = {}) => {
        let filters = { ...rawFilters };
        let reqConfig = {};

        // 1. Process CVSS filter safely with default boundary fallbacks (0 to 10)
        if (filters.cvss !== undefined && filters.cvss !== null && filters.cvss !== "") {
            const parts = String(filters.cvss).split(";").map((p) => p.trim());
            const minStr = parts[0] || "";
            const maxStr = parts.length >= 2 ? (parts[1] || "") : "";

            let min = minStr !== "" && !isNaN(parseFloat(minStr)) ? parseFloat(minStr) : 0;
            let max = maxStr !== "" && !isNaN(parseFloat(maxStr)) ? parseFloat(maxStr) : 10;
            if (parts.length === 1 && minStr !== "" && !isNaN(parseFloat(minStr))) {
                min = parseFloat(minStr);
                max = 10;
            }

            if (min < 0 || isNaN(min)) min = 0;
            if (max > 10 || isNaN(max)) max = 10;

            if (!(min === 0 && max === 10)) {
                reqConfig.cvss__gte = min;
                reqConfig.cvss__lte = max;
            }
        } else if (filters.cvss__gte !== undefined || filters.cvss__lte !== undefined) {
            let min = filters.cvss__gte !== undefined && !isNaN(parseFloat(filters.cvss__gte)) ? parseFloat(filters.cvss__gte) : 0;
            let max = filters.cvss__lte !== undefined && !isNaN(parseFloat(filters.cvss__lte)) ? parseFloat(filters.cvss__lte) : 10;
            
            if (min < 0 || isNaN(min)) min = 0;
            if (max > 10 || isNaN(max)) max = 10;

            if (!(min === 0 && max === 10)) {
                reqConfig.cvss__gte = min;
                reqConfig.cvss__lte = max;
            }
        }

        // 2. Process EPSS filter safely with default boundary fallbacks (0% to 100%)
        if (filters.epss !== undefined && filters.epss !== null && filters.epss !== "") {
            const parts = String(filters.epss).split(";").map((p) => p.trim());
            const minStr = parts[0] ? parts[0].replace("%", "") : "";
            const maxStr = parts.length >= 2 ? (parts[1] ? parts[1].replace("%", "") : "") : "";

            let minVal = minStr !== "" && !isNaN(parseFloat(minStr)) ? parseFloat(minStr) : 0;
            let maxVal = maxStr !== "" && !isNaN(parseFloat(maxStr)) ? parseFloat(maxStr) : 100;
            if (parts.length === 1 && minStr !== "" && !isNaN(parseFloat(minStr))) {
                minVal = parseFloat(minStr);
                maxVal = 100;
            }

            if (minVal < 0 || isNaN(minVal)) minVal = 0;
            if (maxVal > 100 || isNaN(maxVal)) maxVal = 100;

            let minDec = minVal / 100;
            let maxDec = maxVal / 100;

            if (!(minDec === 0 && maxDec === 1)) {
                reqConfig.epss__gte = Number(minDec.toFixed(4));
                reqConfig.epss__lte = Number(maxDec.toFixed(4));
            }
        } else if (filters.epss__gte !== undefined || filters.epss__lte !== undefined) {
            let minDec = filters.epss__gte !== undefined && !isNaN(parseFloat(filters.epss__gte)) ? parseFloat(filters.epss__gte) : 0;
            let maxDec = filters.epss__lte !== undefined && !isNaN(parseFloat(filters.epss__lte)) ? parseFloat(filters.epss__lte) : 1;
            
            if (minDec < 0 || isNaN(minDec)) minDec = 0;
            if (maxDec > 1 || isNaN(maxDec)) maxDec = 1;

            if (!(minDec === 0 && maxDec === 1)) {
                reqConfig.epss__gte = Number(minDec.toFixed(4));
                reqConfig.epss__lte = Number(maxDec.toFixed(4));
            }
        }

        // 3. Process remaining standard filters cleanly
        const ignoreKeys = ["cvss", "epss", "cvss__gte", "cvss__lte", "epss__gte", "epss__lte"];
        for (let [k, v] of Object.entries(filters)) {
            if (ignoreKeys.includes(k)) continue;
            if (k === "cisa_added_at__isnull" && v === "all") continue;

            if (v && typeof v === "object" && Array.isArray(v)) {
                if (v.length > 0 && v[0] !== "" && v[0] !== null && v[0] !== undefined) {
                    reqConfig[k] = v.join(",");
                }
            } else if (v !== "" && v !== null && v !== undefined && v !== "NaN") {
                reqConfig[k] = v;
            }
        }

        return { reqConfig };
    };

    const buildInputs = (tagsList, assocList, cvssVals, epssVals) => {
        return [
            [
                {
                    type: "multiple",
                    name: "level__in",
                    id: "advancedSearchPriorityVulsList",
                    label: $format("vulnerability.severity.title"),
                    selectAll: true,
                    options: [
                        {
                            name:
                                $format("vulnerability.severity.critical")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.critical").slice(1),
                            id: "critical",
                        },
                        {
                            name:
                                $format("vulnerability.severity.high")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.high").slice(1),
                            id: "high",
                        },
                        {
                            name:
                                $format("vulnerability.severity.medium")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.medium").slice(1),
                            id: "medium",
                        },
                        {
                            name:
                                $format("vulnerability.severity.low")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.low").slice(1),
                            id: "low",
                        },
                        {
                            name:
                                $format("vulnerability.severity.info")
                                    .charAt(0)
                                    .toUpperCase() +
                                $format("vulnerability.severity.info").slice(1),
                            id: "info",
                        },
                    ],
                },
                {
                    type: "select",
                    name: "id__ilike",
                    id: "vulTypeFilter",
                    label: $format("components.advancedSearch.vulnerability_kind"),
                    options: [
                        { name: $format("vulnerability.kind.ad"), id: "AD-" },
                        { name: $format("vulnerability.kind.asvs"), id: "ASVS-" },
                        { name: $format("vulnerability.kind.cve"), id: "CVE-" },
                        { name: $format("vulnerability.kind.info"), id: "INFO-" },
                        { name: $format("vulnerability.kind.leak"), id: "LEAK-" },
                        { name: $format("vulnerability.kind.nvt"), id: "NVT-" },
                        { name: $format("vulnerability.kind.sve"), id: "SVE-" },
                        { name: $format("vulnerability.kind.eol"), id: "EOL-" },
                        { name: $format("vulnerability.kind.dmarc"), id: "DMARC-" },
                        { name: $format("vulnerability.kind.spf"), id: "SPF-" },
                        { name: $format("vulnerability.kind.m365"), id: "M365-" },
                        { name: $format("vulnerability.kind.code"), id: "CODE-" },
                        { name: $format("vulnerability.kind.custom"), id: "X-" },
                    ],
                },
            ],
            [
                {
                    type: "numrange",
                    name: "cvss",
                    id: "cvssNumRange",
                    label: "CVSS",
                    values: cvssVals,
                },
                {
                    type: "numrange",
                    name: "epss",
                    id: "epssNumRange",
                    label: "EPSS",
                    values: epssVals,
                },
            ],
            [
                {
                    type: "select",
                    name: "tag_id",
                    id: "vulnerabilityTagMultiple",
                    label: $format("components.advancedSearch.vulnerability_tag"),
                    options: tagService.filterTagsByTargetObjectType(
                        tagsList,
                        assocList,
                        "Vulnerability",
                    ),
                },
                {
                    type: "radio",
                    name: "cisa_added_at__isnull",
                    id: "cisaAddedAtAdvSearch",
                    label: $format("components.advancedSearch.cisa_kev"),
                    options: [
                        { label: $format("yes"), value: false },
                        { label: $format("no"), value: true },
                        { label: $format("all_female"), value: "all" },
                    ],
                },
            ],
        ];
    };

    // Unified data loader triggered cleanly by URL parameter changes
    const loadVulnerabilityData = async (urlParams = {}) => {
        if (cvssValues.length === 0) cvssValues = createCVSSvalues();
        if (epssValues.length === 0) epssValues = createEPSSvalues();

        const [tagsRes, assocRes] = await Promise.all([
            getTags(),
            getTagAssociations(),
        ]);

        // CRITICAL FIX: Only build inputs once on initial load. Reassigning the inputs array
        // on subsequent searches causes Svelte 5 to unmount/destroy form elements mid-interaction!
        if (inputs.length === 0) {
            inputs = buildInputs(tagsRes, assocRes, cvssValues, epssValues);
        } else if (inputs[2] && inputs[2][0]) {
            // Update tag options in place without unmounting the form elements
            inputs[2][0].options = tagService.filterTagsByTargetObjectType(
                tagsRes,
                assocRes,
                "Vulnerability",
            );
        }

        const { reqConfig } = handleFiltersConfig(urlParams);

        await searchVulnerabilities("v2/vulnerability/", {
            filter: reqConfig,
            buildList: {
                tags: { elements: tagsRes, associations: assocRes },
            },
        });

        if (isInitialLoading) isInitialLoading = false;
    };

    // Automatically fetch data on initial load and whenever URL parameters change
    $effect(() => {
        const currentUrlParams = JSON.parse(JSON.stringify($params || {}));
        untrack(() => {
            loadVulnerabilityData(currentUrlParams);
        });
    });

    const handleAdvancedSearch = (event) => {
        let filters = event.detail || {};
        let { reqConfig } = handleFiltersConfig(filters);
        // Defer navigation by a macro-task so AdvancedSearch finishes its DOM event loop completely
        setTimeout(() => {
            redirect(".", reqConfig);
        }, 0);
    };

    const handleClearFilter = () => {
        redirect(".");
    };

    const handleRefresh = async (config = { hydrateWith: [], filter: {} }) => {
        let { reqConfig } = handleFiltersConfig($params || {});
        await searchVulnerabilities("v2/vulnerability/", {
            ...config,
            filter: { ...reqConfig, ...(config.filter || {}) },
            buildList: {
                tags: { elements: tags, associations: associations },
            },
        });
    };

    /* START GRAPHICS FUNCTIONS */
    const getVulCount = async () => {
        if (!token.access_token) return {};
        let vulCount = await statisticService.get("vulnerability-count");
        return {
            critical: vulCount.critical,
            high: vulCount.high,
            medium: vulCount.medium,
            low: vulCount.low,
        };
    };

    const getTopFiveVulsWithFindings = (vulsList) => {
        let topFive = [];
        if (vulsList && vulsList.length > 0) {
            let orderedVuls = [...vulsList].sort((a, b) => {
                return (
                    b.count_by_status.open +
                    b.count_by_status.accepted -
                    (a.count_by_status.open + a.count_by_status.accepted)
                );
            });
            topFive = orderedVuls.slice(0, 5);
        }
        return topFive;
    };

    const groupVulnerabilitiesByKind = (vulsList) => {
        let totals = {
            AD: 0,
            ASVS: 0,
            CVE: 0,
            INFO: 0,
            LEAK: 0,
            NVT: 0,
            EOL: 0,
            DMARC: 0,
            SPF: 0,
            M365: 0,
            CODE: 0,
        };
        if (vulsList && vulsList.length > 0) {
            vulsList.forEach((vul) => {
                if (vul) {
                    let vul_kind = vul.id ? vul.id.split("-")[0] : "";
                    let kinds = Object.keys(totals).join(",");
                    if (kinds.includes(vul_kind)) {
                        totals[vul_kind]++;
                    }
                }
            });
        }
        return totals;
    };

    let vulsByKind = $derived.by(() =>
        groupVulnerabilitiesByKind(vulnerabilities.data || []),
    );
    let topFiveVuls = $derived.by(() =>
        getTopFiveVulsWithFindings(vulnerabilities.data || []),
    );
    /* END GRAPHICS FUNCTIONS */

    let ptOptions = {
        uniquePrefix: "vulnerabilityTable",
        rowsPerPageOptions: [10, 20, 50, 100],
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
    };

    let ptInstructs = [
        { key: "select", title: "", filterable: false, sortable: false },
        {
            key: "priority",
            title: $format("list.columns.priority"),
            filterable: false,
            parseAs: "unsafe-html",
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "title_es",
            title: $format("list.columns.title"),
            filterable: false,
            parseAs: "component",
            dataComponent: VulnerabilityTitleRow,
        },
        {
            key: "open",
            title: $format("list.columns.open"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "accepted",
            title: $format("list.columns.accepted"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "fixed",
            title: $format("list.columns.fixed"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "false_positive",
            title: $format("list.columns.false_positive"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "cvss",
            title: "CVSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "epss",
            title: "EPSS",
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
        {
            key: "tag",
            title: $format("list.columns.tag"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ];
</script>

{#if isInitialLoading}
    <div
        class="d-flex flex-column justify-content-center"
        style="height: 80vh;"
    >
        <Spinner />
    </div>
{:else}
    <section class="content-header">
        <div class="container-fluid">
            <div class="row">
                <div class="col-6">
                    <h1 class="mt-3 mb-3 page-title">
                        <i class="fa-xs fas fa-skull-crossbones"></i>
                        {$format(`vuls`)}
                    </h1>
                </div>
                <div class="col-6">
                    <BreadCrumNav routes={[{ label: $format(`vuls`) }]} />
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            {#await getVulCount() then severityTotals}
                <SeverityTotals
                    totals={severityTotals}
                    totals_by_status={null}
                    redirectTo={"vulnerability"}
                    title={$format(
                        "page.dashboard.components.vulnerabilitiesBySeverity.title",
                    )}
                />
            {/await}
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="card mt-3 card-secondary card-outline">
                <div class="card-header">
                    <h4 class="card-title no-export">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    <!-- CRITICAL FIX: Wrapped AdvancedSearch in a #key block and added clearFilter handler -->
                    {#key JSON.stringify(advancedSearchFilter)}
                        {#if vulnerabilities.loading || vulnerabilities.list.loading}
                            <div class="overlay-wrapper">
                                <div
                                    class="text-center"
                                    class:overlay={vulnerabilities.loading ||
                                        vulnerabilities.list.loading}
                                >
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        {/if}
                        <AdvancedSearch
                            config={{
                                inputs: inputs,
                                activeFilters: advancedSearchFilter,
                            }}
                            on:advancedSearch={handleAdvancedSearch}
                            on:clearFilter={handleClearFilter}
                        />
                    {/key}
                </div>
            </div>
        </div>
    </section>
    <section class="content">
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    {#if vulnerabilities.loading}
                        <div class="overlay-wrapper">
                            <div
                                class="text-center"
                                class:overlay={vulnerabilities.loading}
                            >
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>
                    {/if}
                    <div class="d-flex" style="gap: 15px;">
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format("page.vulnerability.vuls_by_kind")}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !vulnerabilities.list.loading}
                                    {#if vulnerabilities.data.length > 0}
                                        {#key vulsByKind}
                                            <VulsByKind
                                                vulCount={vulsByKind}
                                                {handleAdvancedSearch}
                                                {advancedSearchFilter}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                        <div class="card mt-3" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title no-export">
                                    {$format(
                                        "page.vulnerability.top_five_vuls",
                                    )}
                                </div>
                            </div>
                            <div class="card-body">
                                {#if !vulnerabilities.list.loading}
                                    {#if vulnerabilities.data.length > 0}
                                        {#key topFiveVuls}
                                            <TopFiveVulsByFindings
                                                vulnerabilities={topFiveVuls}
                                            />
                                        {/key}
                                    {:else}
                                        <div
                                            class="chart-has-no-data d-flex flex-column justify-content-center text-center"
                                            style="min-height: 250px;"
                                        >
                                            {$format("results_not_found")}
                                        </div>
                                    {/if}
                                {:else}
                                    <Spinner />
                                {/if}
                            </div>
                        </div>
                    </div>
                    <List
                        {tags}
                        showActions={true}
                        {ptInstructs}
                        {ptOptions}
                        {handleRefresh}
                        graphicsData={{
                            vulsByKind,
                            topFiveVuls
                        }}
                    />
                </div>
            </div>
        </div>
    </section>
{/if}

<style>
</style>